import {
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
  lazy,
  Suspense,
  useRef,
} from "react";
import { InputGroup } from "react-bootstrap";
import Select from "react-select";
import { ArrayHelper, DEFAULT_PAGINATION_VALUES } from "utils";
import { Tercero } from "models/ECF/Tercero";
import { TerceroManager } from "managers";
import ToolTip from "components/ToolTip/ToolTip";
import { GridView } from "components/GridView/GridView";
import IconButton from "components/IconButton/IconButton";
import useExceptionHandler from "hooks/useExceptionHandler";
import { AutoComplete, SvgComponent } from "components";
import SuspenseECF from "components/Suspense/SuspenseECF";
const Simplebar = lazy(() => import("components/Simplebar/Simplebar"));
const TerceroEdit = lazy(() => import("views/Tercero/TerceroEdit"));
import usePaginationValuesWhere from "hooks/usePaginationValuesWhere";
import { AnimatedIconButton } from "components/AnimatedIconButton/AnimatedIconButton";
import {
  buttonStyle,
  controlsProportion,
  initialWhere,
  initialWhereModal,
} from "..";
import "../Lookup.style.scss";

export const TerceroLookup = forwardRef(
  (
    {
      isDisabled,
      idSecList = [],
      isInput = false,
      isMultiselect = false,
      onChange = () => { },
      onMultiselectChange = () => { },
      initialValue = { label: "", value: "" },
      initialValueList = [],
      limpiar = false,
      required = false,
      loadOnShowModal = false,
      loadValue = false,
      whereFijo = {},
    },
    ref
  ) => {
    const isMounted = useRef(true);
    const [showCanvas, setShowCanvas] = useState(false);
    const [showTerceroEditCanvas, setShowTerceroEditCanvas] = useState(false);
    const [terceroListModal, setTerceroListModal] = useState([]);
    const [whereModal, setWhereModal] = useState(initialWhereModal);
    const [pagination, setPagination] = useState(DEFAULT_PAGINATION_VALUES);
    const [selectedValue, setSelectedValue] = useState({
      label: "",
      value: "",
      documento: "",
      email: "",
      direccion: "",
    });
    const [multiSelectedValue, setMultiSelectedValue] = useState([]);

    const ESTATUS_LIST = [
      { value: "", label: "Todos" },
      ...(Tercero.ESTATUS_LIST || []),
    ];

    const TIPO_DOCUMENTO_LIST = [
      { value: "", label: "Todos" },
      ...(Tercero.TIPO_DOCUMENTO_LIST || []),
    ];

    const TIPO_COMPROBANTE_LIST = [
      { value: "", label: "Todos" },
      ...(Tercero.TIPO_COMPROBANTE_LIST || []),
    ];

    const { handleException } = useExceptionHandler();
    const { setPaginationValuesWhere } = usePaginationValuesWhere();

    useEffect(() => {
      return () => {
        isMounted.current = false;
      };
    }, []);

    useEffect(() => {
      if (limpiar) {
        handleMultiselectChange([]);
      }
    }, [limpiar]);

    useEffect(() => {
      if (!loadOnShowModal) {
        buscar();
      }
    }, [whereModal, JSON.stringify(whereFijo)]);

    useEffect(() => {
      if (
        initialValue &&
        initialValue.value &&
        initialValue.value !== selectedValue.value
      ) {
        if (loadValue && !initialValue.label) buscarPorId(initialValue.value);
        else setSelectedValue(initialValue);
      }
    }, [initialValue.value]);

    useEffect(() => {
      if (initialValueList && initialValueList.length > 0) {
        setMultiSelectedValue(initialValueList);
      }
    }, [initialValueList.length]);

    const handleMultiselectChange = (value) => {
      setMultiSelectedValue(value ? value : []);
      onMultiselectChange(value ? value : []);
    };

    const handleSelectChange = (value) => {
      setSelectedValue(value ? value : {});
      onChange(value ? value : {});
    };

    function buscar() {
      const whereWithPaginationValues = {
        Estatus: Tercero.DEFAULT_ESTATUS_VALUE,
        ...whereFijo,
        ...whereModal,
        ...pagination,
        IdSecList: idSecList,
      };
      TerceroManager
        .listado(whereWithPaginationValues)
        .then((res) => {
          if (isMounted.current) {
            setPaginationValuesWhere(res.Headers, pagination, setPagination);
            setTerceroListModal(res.Data);
          }
        })
        .catch((err) => handleException(err));
    }

    const buscarPorId = (idsec) => {
      TerceroManager
        .buscarPorId({ IdSec: idsec, EsLookup: true })
        .then((res) => {
          setSelectedValue({ label: res.label, value: res.value });
        })
        .catch((err) => handleException(err));
    };

    const handleCerrar = () => {
      setShowCanvas(false);
      setWhereModal(initialWhereModal);
    };

    const onPageIndexChanging = (pageNumber) => {
      setPagination((prevstate) => {
        return { ...prevstate, IndiceDePagina: pageNumber - 1 };
      });
      setWhereModal({ ...whereModal });
    };

    const getSuggestions = async (value) => {
      const inputValue = value.trim().toLowerCase();
      const res = await TerceroManager
        .listado({
          ...initialWhere,
          ...whereFijo,
          RazonSocial: inputValue,
          IdSecList: idSecList,
          EsLookup: true,
        });
      return res.Data;
    };

    const addToList = (newItem) => {
      setMultiSelectedValue((prev) => (prev ? [...prev, newItem] : [newItem]));
    };

    useImperativeHandle(ref, () => ({
      addToList,
      openTerceroEdit: () => setShowTerceroEditCanvas(true),
    }));

    const renderAutoComplete = () => (
      <AutoComplete
        required={required}
        isDisabled={isDisabled}
        getList={getSuggestions}
        noBorderRadius={isInput}
        isMultiselect={isMultiselect}
        placeholder={isMultiselect ? "Terceros" : "Tercero"}
        parentValue={isMultiselect ? multiSelectedValue : selectedValue}
        setParentValue={
          isMultiselect ? handleMultiselectChange : handleSelectChange
        }
      />
    );

    return (
      <>
        {!isInput ? (
          <InputGroup className="w-100">
            <div style={{ width: controlsProportion.input }}>
              {renderAutoComplete()}
            </div>
            {!isDisabled && (
              <InputGroup.Append style={{ width: controlsProportion.button }}>
                <AnimatedIconButton
                  size="40px"
                  height="18"
                  width="18"
                  message="Seleccionar Tercero"
                  style={buttonStyle}
                  disabled={isDisabled}
                  iconName="search"
                  onClick={() => {
                    setShowCanvas(true);
                    setTimeout(() => buscar(), 200);
                  }}
                />
              </InputGroup.Append>
            )}
          </InputGroup>
        ) : (
          <>
            <div style={{ width: controlsProportion.inputGroup }}>
              {renderAutoComplete()}
            </div>
            {!isDisabled && (
              <InputGroup.Append style={{ width: controlsProportion.button }}>
                <AnimatedIconButton
                  size="40px"
                  height="18"
                  width="18"
                  message="Seleccionar Tercero"
                  style={buttonStyle}
                  disabled={isDisabled}
                  iconName="search"
                  onClick={() => {
                    setShowCanvas(true);
                    setTimeout(() => buscar(), 200);
                  }}
                />
              </InputGroup.Append>
            )}
          </>
        )}

        {showTerceroEditCanvas && (
          <Suspense fallback={<SuspenseECF />}>
            <TerceroEdit
              IdSec={undefined}
              showCanvas={showTerceroEditCanvas}
              setShowCanvas={setShowTerceroEditCanvas}
              showLoading={() => {}}
              hideLoading={() => {}}
              updateList={buscar}
              onCreated={(res) => {
                handleSelectChange({
                  label: res.RazonSocial,
                  value: res.IdSec,
                  RazonSocial: res.RazonSocial,
                  TipoDocumento: res.TipoDocumento,
                  Documento: res.Documento,
                  TipoComprobanteVenta:  res.TipoComprobanteVenta,
                  TipoComprobanteCompra: res.TipoComprobanteCompra,
                });
              }}
            />
          </Suspense>
        )}

        {showCanvas && (
          <Suspense fallback={<SuspenseECF />}>
            <Simplebar
              backdrop={true}
              placement={"end"}
              portal={false}
              size="xl"
              useLoading={true}
              visible={showCanvas}
              setShow={(e) => setShowCanvas(e)}
              title="Seleccionar Tercero"
              btnLeft={{ show: false, func: () => { } }}
              btnRight={{ show: false, func: () => { } }}
            >
              <GridView
                pagination={pagination}
                setPagination={setPagination}
                allowPaging={pagination.EsPaginable}
                pageSize={pagination.CantidadPorPagina}
                pageIndex={pagination.IndiceDePagina}
                lastPageNumber={pagination.PaginasTotal}
                dataSource={terceroListModal}
                onPageIndexChanging={onPageIndexChanging}
                where={whereModal}
                setWhere={setWhereModal}
              >
                <GridView.Column
                  isSticky
                  bodyRenderer={(row) => (
                    <div className="float-right">
                      <IconButton
                        onClick={() => {
                          const selectedItem = {
                            label: row.RazonSocial,
                            value: row.IdSec,
                            RazonSocial: row.RazonSocial,
                            TipoDocumento: row.TipoDocumento,
                            Documento: row.Documento,
                            TipoComprobanteVenta:  row.TipoComprobanteVenta,
                            TipoComprobanteCompra: row.TipoComprobanteCompra,
                          };
                          let values = multiSelectedValue || [];
                          if (isMultiselect) {
                            handleMultiselectChange(
                              ArrayHelper.removeObjectsDuplicated(
                                [...values, selectedItem],
                                ["value", "label"]
                              )
                            );
                          } else {
                            handleSelectChange(selectedItem);
                          }
                          handleCerrar();
                        }}
                      >
                        <ToolTip message="Seleccionar">
                          <div>
                            <SvgComponent height="20" width="20" iconName="check" />
                          </div>
                        </ToolTip>
                      </IconButton>
                    </div>
                  )}
                />
                <GridView.Column
                  headerText="Id"
                  dataField="IdSec"
                  sortExpression="IdSec"
                  width="80px"
                />
                <GridView.Column
                  headerText="Tipo Documento"
                  dataField="TipoDocumentoDesc"
                  sortExpression="TipoDocumentoDesc"
                  width="150px"
                  filterRenderer={() => (
                    <Select
                      className="react-select primary"
                      options={TIPO_DOCUMENTO_LIST}
                      value={TIPO_DOCUMENTO_LIST.filter(
                        (opt) => opt.value === (whereModal.TipoDocumento || TIPO_DOCUMENTO_LIST[0].value)
                      )}
                      name="TipoDocumento"
                      onChange={(e) =>
                        setWhereModal({ ...whereModal, TipoDocumento: e.value })
                      }
                    />
                  )}
                />
                <GridView.Column
                  headerText="Documento"
                  dataField="Documento"
                  sortExpression="Documento"
                  width="180px"
                />
                <GridView.Column
                  headerText="Razon Social"
                  dataField="RazonSocial"
                  sortExpression="RazonSocial"
                  width="250px"
                />
                <GridView.Column
                  headerText="Tipo Comprobante Venta"
                  dataField="TipoComprobanteVentaDesc"
                  sortExpression="TipoComprobanteVentaDesc"
                  width="180px"
                  filterRenderer={() => (
                    <Select
                      className="react-select primary"
                      options={TIPO_COMPROBANTE_LIST}
                      value={TIPO_COMPROBANTE_LIST.filter(
                        (opt) => opt.value === (whereModal.TipoComprobanteVenta || TIPO_COMPROBANTE_LIST[0].value)
                      )}
                      name="TipoComprobanteVenta"
                      onChange={(e) =>
                        setWhereModal({ ...whereModal, TipoComprobanteVenta: e.value })
                      }
                    />
                  )}
                />
                <GridView.Column
                  headerText="Tipo Comprobante Compra"
                  dataField="TipoComprobanteCompraDesc"
                  sortExpression="TipoComprobanteCompraDesc"
                  width="180px"
                  filterRenderer={() => (
                    <Select
                      className="react-select primary"
                      options={TIPO_COMPROBANTE_LIST}
                      value={TIPO_COMPROBANTE_LIST.filter(
                        (opt) => opt.value === (whereModal.TipoComprobanteCompra || TIPO_COMPROBANTE_LIST[0].value)
                      )}
                      name="TipoComprobanteCompra"
                      onChange={(e) =>
                        setWhereModal({ ...whereModal, TipoComprobanteCompra: e.value })
                      }
                    />
                  )}
                />
              </GridView>
            </Simplebar>
          </Suspense>
        )}
      </>
    );
  }
);
